import React, { useState, useCallback, useEffect, useRef } from 'react';
import FileExplorer from './FileExplorer';
import EditorHeader from './EditorHeader';
import EditorPane from './EditorPane';
import StatusBar from './StatusBar';
import OutputPane from './OutputPane';
import AIFeedbackPane from './AIFeedbackPane';
import CodeEditorSettings from './CodeEditorSettings';
import ProjectModal from './ProjectModal';
import FileModal from './FileModal';
import ErrorBoundary from './ErrorBoundary';
import RubricSidebar from '../Shared/RubricSidebar';
import SubmissionHistorySidebar from '../Shared/SubmissionHistorySidebar';
import useCodeEditor from './hooks/useCodeEditor';
import { useCodeEditorResizing } from './hooks/useCodeEditorResizing';
import { useRealtimeCodeEditor } from '../../hooks/useRealtimeCodeEditor';
import { codeEditorApi } from '../../utils/api/CodeEditorApi';
import { getEditorSettingsApi } from '../../utils/api/EditorSettingsApi';
import { lookupAssignment, createSubmission, submitSubmission } from '../../utils/api/AssignmentsApi';

const CodeEditor = ({ username, userRole, apiBaseUrl, classContext, assignmentId = null, componentId = null, initialProjectId = null, initialFileId = null }) => {
  // Performance logging
  useEffect(() => {
    const startTime = performance.now();
    console.log('[CodeEditor] Component mounting...');
    
    return () => {
      const loadTime = performance.now() - startTime;
      console.log(`[CodeEditor] Component mounted in ${loadTime.toFixed(2)}ms`);
    };
  }, []);
  const containerRef = useRef(null);
  const outputPaneRef = useRef(null);
  const [showProjectModal, setShowProjectModal] = useState(false);
  const [showFileModal, setShowFileModal] = useState(false);
  const [selectedProjectForFile, setSelectedProjectForFile] = useState(null);
  const [showOutput, setShowOutput] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showRubricSidebar, setShowRubricSidebar] = useState(false);
  const [showSubmissionsSidebar, setShowSubmissionsSidebar] = useState(false);

  // Auto-detected assignment context
  const [detectedAssignmentId, setDetectedAssignmentId] = useState(null);
  const [detectedComponentId, setDetectedComponentId] = useState(null);
  const effectiveAssignmentId = assignmentId || detectedAssignmentId;
  const effectiveComponentId = componentId || detectedComponentId;

  // Submission state
  const [isSubmittingAssignment, setIsSubmittingAssignment] = useState(false);
  const [assignmentData, setAssignmentData] = useState(null); // full assignment for due date checks

  const [settings, setSettings] = useState({
    outputPlacement: 'bottom' // 'bottom' or 'right'
  });
  const [settingsLoaded, setSettingsLoaded] = useState(false);

  // Lift output pane state to parent to persist across placement changes
  const [executionHistory, setExecutionHistory] = useState([]);
  const [currentOutput, setCurrentOutput] = useState(null);
  const [isExecuting, setIsExecuting] = useState(false);

  // Suppress harmless ResizeObserver loop errors - this is a known browser quirk
  // when ResizeObserver callbacks take slightly too long during animations
  useEffect(() => {
    // Suppress the error message in console
    const originalError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver loop')) {
        return; // Suppress this specific error
      }
      originalError.apply(console, args);
    };

    // Override window.onerror to prevent React error overlay
    const originalOnError = window.onerror;
    window.onerror = (message, source, lineno, colno, error) => {
      if (typeof message === 'string' && message.includes('ResizeObserver loop')) {
        return true; // Prevent default error handling
      }
      return originalOnError ? originalOnError(message, source, lineno, colno, error) : false;
    };

    // Catch the error event before React does
    const handleError = (event) => {
      if (event.message?.includes('ResizeObserver loop')) {
        event.stopImmediatePropagation();
        event.preventDefault();
        return true;
      }
    };

    // Catch unhandled promise rejections
    const handleRejection = (event) => {
      if (event.reason?.message?.includes('ResizeObserver loop')) {
        event.stopImmediatePropagation();
        event.preventDefault();
      }
    };

    window.addEventListener('error', handleError, true); // Use capture phase
    window.addEventListener('unhandledrejection', handleRejection, true);

    return () => {
      console.error = originalError;
      window.onerror = originalOnError;
      window.removeEventListener('error', handleError, true);
      window.removeEventListener('unhandledrejection', handleRejection, true);
    };
  }, []);

  // Initialize resizing hook
  const resizing = useCodeEditorResizing(containerRef);

  // Use real-time code editor hook (only connect when actually rendered)
  const {
    projects: realtimeProjects,
    standaloneFiles,
    loading: realtimeLoading,
    error: realtimeError,
    createProject: createProjectOptimistic,
    createFile: createFileOptimistic,
    updateFile: updateFileOptimistic,
    refresh,
    hasPendingUpdates
  } = useRealtimeCodeEditor(classContext, { enabled: true });

  // Use traditional code editor hook for editor functionality
  const {
    // State
    code,
    language,
    theme,
    activeFile,
    showExplorer,
    loading: editorLoading,
    error: editorError,
    languages,
    themes,

    // Autosave state
    saving,
    lastSaveTime,

    // AI Feedback state
    aiFeedback,

    // Actions
    setShowExplorer,
    handleEditorChange,
    openFile,
    toggleFolder,
    switchProject,
    clearError,

    // AI Feedback actions
    refreshAIFeedback,
    toggleAIFeedback
  } = useCodeEditor(classContext);

  // Combine loading states
  const loading = realtimeLoading || editorLoading;

  // Prioritize real-time error over editor error
  const error = realtimeError || editorError;

  // Use real-time data for file/project lists
  const projects = realtimeProjects;
  const files = standaloneFiles;

  // Load user's editor settings from backend
  useEffect(() => {
    const loadUserSettings = async () => {
      if (!username || settingsLoaded) return;

      try {
        // Note: This app uses session-based auth
        // The browser automatically sends session cookies with fetch requests
        const editorSettingsApi = getEditorSettingsApi(apiBaseUrl, null, username, userRole);
        const result = await editorSettingsApi.getUserEditorSettings(username);

        if (result.success && result.settings) {
          console.log('Loaded user editor settings:', result.settings);
          setSettings(result.settings);
        } else {
          console.warn('Failed to load user settings, using defaults:', result.error);
          // Keep the default settings
        }
      } catch (error) {
        console.error('Error loading user editor settings:', error);
        // Keep the default settings
      } finally {
        setSettingsLoaded(true);
      }
    };

    loadUserSettings();
  }, [username, apiBaseUrl, settingsLoaded]);

  // Auto-open file from navigation data (e.g., from Rubric Builder "Open in Editor")
  // Capture initial values in refs since App.jsx clears navigationData before loading finishes
  const initialFileIdRef = useRef(initialFileId);
  const [initialFileOpened, setInitialFileOpened] = useState(false);
  useEffect(() => {
    console.log('[CodeEditor] Auto-open effect:', {
      initialFileId: initialFileIdRef.current,
      initialFileOpened,
      realtimeLoading,
      projectCount: projects.length,
      fileCount: files.length
    });
    if (initialFileOpened || !initialFileIdRef.current || realtimeLoading) return;
    const targetFileId = initialFileIdRef.current;

    // Search for the file in projects
    for (const project of projects) {
      const foundFile = (project.files || []).find(f => f.file.file_id === targetFileId);
      if (foundFile) {
        console.log('[CodeEditor] Auto-opening file from project:', foundFile.file.file_name || foundFile.file.name);
        openFile(foundFile);
        setInitialFileOpened(true);
        return;
      }
    }

    // Search in standalone files
    const foundStandalone = files.find(f => f.file.file_id === targetFileId);
    if (foundStandalone) {
      console.log('[CodeEditor] Auto-opening standalone file:', foundStandalone.file.file_name || foundStandalone.file.name);
      openFile(foundStandalone);
      setInitialFileOpened(true);
    } else {
      console.log('[CodeEditor] File not found with id:', targetFileId);
    }
  }, [realtimeLoading, projects, files, initialFileOpened]);

  // Auto-detect assignment when active file changes (always runs regardless of prop)
  useEffect(() => {
    const fileId = activeFile?.file?.file_id;
    if (!fileId || !classContext) {
      setDetectedAssignmentId(null);
      setDetectedComponentId(null);
      return;
    }

    lookupAssignment({ file_id: fileId }, classContext)
      .then(result => {
        if (result.success && result.assignment) {
          setDetectedAssignmentId(result.assignment.assignment_id);
          setAssignmentData(result.assignment);
          const comp = (result.assignment.components || []).find(c => c.type === 'code');
          setDetectedComponentId(comp?.component_id || null);
        } else {
          setDetectedAssignmentId(null);
          setDetectedComponentId(null);
          setAssignmentData(null);
        }
      })
      .catch(() => {
        setDetectedAssignmentId(null);
        setDetectedComponentId(null);
        setAssignmentData(null);
      });
  }, [activeFile?.file?.file_id]);

  // Compute due date status for submit button
  const getDueDateStatus = () => {
    if (!assignmentData?.due_date) return { canSubmit: true, isLate: false, isLocked: false };
    const now = new Date();
    const due = new Date(assignmentData.due_date);
    if (now <= due) return { canSubmit: true, isLate: false, isLocked: false };

    // Past due date
    const allowLate = assignmentData.allow_late_submission !== false;
    const lockAfter = assignmentData.lock_after_due_date === true;

    if (lockAfter && !allowLate) return { canSubmit: false, isLate: false, isLocked: true };
    if (!allowLate) return { canSubmit: false, isLate: false, isLocked: true };

    // Late submissions allowed - check late deadline
    if (assignmentData.late_due_date) {
      const lateDue = new Date(assignmentData.late_due_date);
      if (now > lateDue) return { canSubmit: false, isLate: false, isLocked: true };
    }

    return { canSubmit: true, isLate: true, isLocked: false };
  };

  const dueDateStatus = getDueDateStatus();

  // Handle assignment submission - always creates a NEW submission
  const handleSubmitAssignment = async () => {
    if (!classContext || !effectiveAssignmentId) return;

    setIsSubmittingAssignment(true);
    try {
      // Always create a new submission (new attempt)
      const createResult = await createSubmission({
        assignment_id: effectiveAssignmentId,
        submission_type: 'code',
        content: {
          code: code,
          file_id: activeFile?.file?.file_id,
          file_name: activeFile?.file?.file_name || activeFile?.file?.name,
          project_id: activeFile?.project_id
        },
        status: 'draft'
      }, classContext);

      if (!createResult.success) throw new Error(createResult.error || 'Failed to create submission');

      const submitResult = await submitSubmission(createResult.submission_id, classContext);
      if (!submitResult.success) throw new Error(submitResult.error || 'Failed to submit');

      const lateMsg = submitResult.is_late ? ' (Late submission - penalty may apply)' : '';
      alert(`Assignment submitted successfully!${lateMsg}`);
    } catch (error) {
      console.error('Submission error:', error);
      alert(error.message || 'Failed to submit assignment');
    } finally {
      setIsSubmittingAssignment(false);
    }
  };

  // Handle project creation from modal with optimistic updates
  const handleCreateProject = async (projectName, runtimeLanguage, description) => {
    try {
      const result = await createProjectOptimistic(projectName, runtimeLanguage, description);

      if (result?.success) {
        setShowProjectModal(false);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to create project:', error);
      return false;
    }
  };

  // Handle file creation from modal with optimistic updates
  const handleCreateFile = async (fileName, projectId) => {
    try {
      // Use the selected project if no project ID is provided
      const targetProjectId = projectId !== undefined ? projectId : selectedProjectForFile;
      const result = await createFileOptimistic(fileName, '', targetProjectId);
      if (result?.success) {
        setShowFileModal(false);
        setSelectedProjectForFile(null); // Reset selection

        // Open the newly created file automatically
        if (result.file) {
          openFile(result.file);
        }
        return true;
      }
      return false;
    } catch (error) {
      console.error('Failed to create file:', error);
      return false;
    }
  };

  // Handle showing file modal with optional project selection
  const handleShowFileModal = (projectId = null) => {
    setSelectedProjectForFile(projectId);
    setShowFileModal(true);
  };

  // Handle moving files between projects
  const handleMoveFileToProject = async (fileId, projectId) => {
    try {
      console.log('Moving file', fileId, 'to project', projectId);

      // Call the HTTP API to move the file
      const response = await codeEditorApi.moveFile(fileId, projectId, classContext);

      if (response.success) {
        console.log('File moved successfully');
        // Refresh data to reflect the move
        refresh();
        return true;
      } else {
        console.error('Failed to move file:', response.error);
        return false;
      }
    } catch (error) {
      console.error('Failed to move file:', error);
      return false;
    }
  };

  // Get current project from realtime data
  const getCurrentProject = () => {
    return projects.find(p => p.project_id === activeFile?.project_id) || null;
  };

  // Monaco editor instance ref (kept for potential future use, but layout is automatic)
  const monacoEditorRef = useRef(null);
  const layoutTimeoutRef = useRef(null);

  // Handle editor ready callback from EditorPane
  const handleEditorReady = useCallback((editor) => {
    monacoEditorRef.current = editor;
    console.log('✅ Monaco editor ready with automaticLayout enabled');
  }, []);

  // Trigger Monaco layout after explorer transition completes to reduce flicker
  useEffect(() => {
    // Clear any pending layout calls
    if (layoutTimeoutRef.current) {
      clearTimeout(layoutTimeoutRef.current);
    }

    // Temporarily disable automatic layout during transition
    if (monacoEditorRef.current) {
      monacoEditorRef.current.updateOptions({ automaticLayout: false });

      // Re-enable automatic layout and force layout after transition completes
      layoutTimeoutRef.current = setTimeout(() => {
        if (monacoEditorRef.current) {
          monacoEditorRef.current.updateOptions({ automaticLayout: true });
          monacoEditorRef.current.layout();
        }
      }, 350);
    }

    return () => {
      if (layoutTimeoutRef.current) {
        clearTimeout(layoutTimeoutRef.current);
      }
    };
  }, [showExplorer, showOutput, aiFeedback.showFeedback]);

  // Disable Monaco automatic layout during manual resizing to prevent minimap flicker
  useEffect(() => {
    if (!monacoEditorRef.current) return;

    if (resizing.isResizingHorizontal || resizing.isResizingVertical || resizing.isResizingCorner) {
      // Disable automatic layout while actively resizing
      monacoEditorRef.current.updateOptions({ automaticLayout: false });
    } else {
      // Re-enable automatic layout when resize completes
      // Small delay to ensure resize state has fully settled
      const timer = setTimeout(() => {
        if (monacoEditorRef.current) {
          monacoEditorRef.current.updateOptions({ automaticLayout: true });
          monacoEditorRef.current.layout();
        }
      }, 50);

      return () => clearTimeout(timer);
    }
  }, [resizing.isResizingHorizontal, resizing.isResizingVertical, resizing.isResizingCorner]);

  // Handle settings changes and save to backend (called by Save button in settings modal)
  const handleSettingsChange = useCallback(async (newSettings) => {
    console.log('[CodeEditor] handleSettingsChange called', {
      username,
      settingsLoaded,
      hasApiBaseUrl: !!apiBaseUrl,
      settingsKeys: Object.keys(newSettings)
    });

    setSettings(newSettings);

    // Save settings to backend
    if (!username) {
      console.error('[CodeEditor] Cannot save settings: username is missing');
      throw new Error('Username is required to save settings');
    }

    if (!settingsLoaded) {
      console.error('[CodeEditor] Cannot save settings: settings not loaded yet');
      throw new Error('Settings must be loaded before saving');
    }

    try {
      // Note: This app uses session-based auth, not JWT tokens
      // The browser automatically sends session cookies with fetch requests
      console.log('[CodeEditor] Calling API to save settings (using session auth)...');
      const editorSettingsApi = getEditorSettingsApi(apiBaseUrl, null, username, userRole);
      const result = await editorSettingsApi.updateUserEditorSettings(username, newSettings);

      if (!result.success) {
        console.error('[CodeEditor] API returned error:', result.error);
        throw new Error(result.error);
      }

      console.log('[CodeEditor] Settings saved successfully to backend');
    } catch (error) {
      console.error('[CodeEditor] Error saving editor settings:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  }, [username, apiBaseUrl, settingsLoaded]);

  // Enhanced file selection that ensures we have the latest content
  const handleFileSelect = useCallback((selectedFile) => {
    // Find the file in the current real-time data to get the latest content
    let latestFileData = null;

    // Search in projects first
    for (const project of projects) {
      const foundFile = (project.files || []).find(f =>
        f.file.file_id === selectedFile.file.file_id
      );
      if (foundFile) {
        latestFileData = foundFile;
        break;
      }
    }

    // If not found in projects, search in standalone files
    if (!latestFileData) {
      latestFileData = files.find(f =>
        f.file.file_id === selectedFile.file.file_id
      );
    }

    // Use the latest data if found, otherwise fall back to selected file
    const fileToOpen = latestFileData || selectedFile;
    openFile(fileToOpen);
  }, [projects, files, openFile]);

  // Handle resetting settings to defaults via backend
  const handleResetSettings = useCallback(async () => {
    if (!username || !settingsLoaded) return;

    try {
      // Note: This app uses session-based auth
      const editorSettingsApi = getEditorSettingsApi(apiBaseUrl, null, username, userRole);
      const result = await editorSettingsApi.resetUserEditorSettings(username);

      if (result.success && result.settings) {
        console.log('Editor settings reset to defaults successfully');
        setSettings(result.settings);
      } else {
        console.error('Failed to reset editor settings:', result.error);
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error resetting editor settings:', error);
      throw error; // Re-throw so the modal can handle the error
    }
  }, [username, apiBaseUrl, settingsLoaded]);

  return (
    <div className="h-full w-full bg-white flex flex-col overflow-hidden">
      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-0">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
              <button
                onClick={clearError}
                className="mt-2 text-sm text-red-600 underline hover:text-red-800"
              >
                Dismiss
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Loading indicator in FileExplorer area - less intrusive than full overlay */}
      {realtimeLoading && projects.length === 0 && standaloneFiles.length === 0 && (
        <div className="absolute top-16 left-0 right-0 bottom-0 bg-white bg-opacity-75 flex items-center justify-center z-40 pointer-events-none">
          <div className="flex flex-col items-center space-y-2 bg-white px-4 py-3 rounded-lg shadow-sm border border-gray-200 pointer-events-auto">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
            <p className="text-sm text-gray-600">Loading files...</p>
          </div>
        </div>
      )}

      {/* Editor Header - Independent container spanning full width */}
      <div className="flex-shrink-0 w-full">
        <EditorHeader
          showExplorer={showExplorer}
          activeFile={activeFile}
          onShowExplorer={() => setShowExplorer(true)}
          onToggleExplorer={() => setShowExplorer(!showExplorer)}
          onSave={async () => {
            if (activeFile) {
              try {
                await updateFileOptimistic(activeFile.file.file_id, code);
              } catch (error) {
                console.error('Failed to save file:', error);
              }
            }
          }}
          onRun={() => {
            setShowOutput(true);
            // Call the OutputPane's runCode method via ref
            if (outputPaneRef.current) {
              outputPaneRef.current.runCode();
            }
          }}
          onStop={() => {
            if (outputPaneRef.current) {
              outputPaneRef.current.stopExecution();
            }
          }}
          isExecuting={isExecuting}
          onSettings={() => setShowSettings(true)}
          saving={saving}
          lastSaveTime={lastSaveTime}
          hasPendingUpdates={hasPendingUpdates}
          // AI Feedback props
          showAIFeedback={aiFeedback.showFeedback}
          onToggleAIFeedback={toggleAIFeedback}
          aiFeedbackCount={aiFeedback.items.length}
          aiFeedbackLoading={aiFeedback.loading}
          // Rubric props
          showRubric={!!effectiveAssignmentId}
          onOpenRubric={() => setShowRubricSidebar(true)}
          // Submissions history props
          showSubmissions={!!effectiveAssignmentId && userRole !== 'Instructor' && userRole !== 'TA'}
          onOpenSubmissions={() => setShowSubmissionsSidebar(true)}
          // Submission props
          assignmentId={effectiveAssignmentId}
          isSubmittingAssignment={isSubmittingAssignment}
          onSubmitAssignment={handleSubmitAssignment}
          dueDateStatus={dueDateStatus}
        />
      </div>

      {/* Main content area with proper flex layout */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* File Explorer with responsive width - FIX: Specific transitions to reduce flicker */}
        <div
          className={`flex-shrink-0 border-r border-gray-200/60 bg-gray-50/80 overflow-hidden ${
            showExplorer
              ? showOutput
                ? 'w-64 opacity-100' // Narrower when output panel is open
                : 'w-80 opacity-100' // Full width when no output panel
              : 'w-0 opacity-0'
          }`}
          style={{
            transition: 'width 300ms ease-in-out, opacity 300ms ease-in-out',
            willChange: showExplorer ? 'width' : 'auto'
          }}
        >
          <div className={showOutput ? 'w-64' : 'w-80'} style={{ height: '100%' }}>
            <FileExplorer
              showExplorer={showExplorer}
              files={files}
              projects={projects}
              currentProject={getCurrentProject()}
              activeFile={activeFile}
              loading={loading}
              onFileSelect={handleFileSelect}
              onFolderToggle={toggleFolder}
              onShowFileModal={handleShowFileModal}
              onShowProjectModal={() => setShowProjectModal(true)}
              onSwitchProject={switchProject}
              onMoveFileToProject={handleMoveFileToProject}
              onRefresh={refresh}
            />
          </div>
        </div>

        {/* Main Editor and Output Container - FIX: Add min-w-0 to prevent overflow */}
        <div ref={containerRef} className="flex-1 flex flex-col min-h-0 min-w-0 overflow-hidden">

          {/* Main Editor Layout - Grid-like structure with flex */}
          <div className="flex-1 flex flex-row min-h-0 min-w-0 overflow-hidden">
            {/* Left/Middle Section: Editor + Bottom Pane - FIX: Add overflow-hidden to contain children */}
            <div className={`flex flex-col min-h-0 min-w-0 overflow-hidden ${
              // When right pane is visible, this section needs to shrink
              (settings.outputPlacement === 'right' && showOutput) ||
              (settings.outputPlacement === 'bottom' && aiFeedback.showFeedback)
                ? 'flex-1'
                : 'flex-1'
            }`}>
              {/* Monaco Editor Container */}
              <div className="min-h-0 min-w-0 flex flex-col flex-1 overflow-hidden">
                <div className="flex-1 min-h-[400px] overflow-hidden">
                  <ErrorBoundary>
                    <EditorPane
                      code={code}
                      language={language}
                      theme={theme}
                      activeFile={activeFile}
                      onChange={handleEditorChange}
                      settings={settings}
                      feedbackItems={aiFeedback.items}
                      showAIFeedbackPane={aiFeedback.showFeedback}
                      onEditorReady={handleEditorReady}
                    />
                  </ErrorBoundary>
                </div>

                {/* Status Bar - only show when no panes are visible OR output is on right */}
                {((!showOutput && !aiFeedback.showFeedback) || (settings.outputPlacement === 'right' && showOutput && !aiFeedback.showFeedback)) && (
                  <StatusBar
                    language={language}
                    theme={theme}
                    activeFile={activeFile}
                    languages={languages}
                    themes={themes}
                  />
                )}
              </div>

              {/* Bottom Pane (Output when outputPlacement='bottom', AI Feedback when outputPlacement='right') */}
              <>
                {/* Resize Handle */}
                <div
                  className="relative horizontal-resize-handle"
                  style={{
                    height: ((settings.outputPlacement === 'bottom' && showOutput) ||
                            (settings.outputPlacement === 'right' && aiFeedback.showFeedback)) ? '8px' : '0px',
                    transition: 'height 300ms ease-in-out',
                    overflow: 'hidden'
                  }}
                >
                  <div
                    className="h-2 bg-gray-300 hover:bg-gray-400 cursor-row-resize flex items-center justify-center transition-colors duration-200 group"
                    onMouseDown={resizing.handleVerticalResizeStart}
                  >
                    <div className="w-16 h-1 bg-gray-500 rounded-full group-hover:bg-gray-600"></div>
                  </div>

                  {/* Corner resize handle - only show when both panes are visible */}
                  {((settings.outputPlacement === 'bottom' && showOutput && aiFeedback.showFeedback) ||
                    (settings.outputPlacement === 'right' && showOutput && aiFeedback.showFeedback)) && (
                    <div
                      className="corner-resize-handle absolute top-0 right-0 w-6 h-6 cursor-nwse-resize z-20 group"
                      onMouseDown={(e) => {
                        document.querySelector('.horizontal-resize-handle > div')?.classList.add('bg-gray-400');
                        document.querySelector('.vertical-resize-handle')?.classList.add('bg-gray-400');
                        resizing.handleCornerResizeStart(e);
                      }}
                      onMouseEnter={() => {
                        document.querySelector('.horizontal-resize-handle > div')?.classList.add('bg-gray-400');
                        document.querySelector('.vertical-resize-handle')?.classList.add('bg-gray-400');
                      }}
                      onMouseLeave={() => {
                        if (!resizing.isResizingCorner) {
                          document.querySelector('.horizontal-resize-handle > div')?.classList.remove('bg-gray-400');
                          document.querySelector('.vertical-resize-handle')?.classList.remove('bg-gray-400');
                        }
                      }}
                      title="Resize both panes"
                    >
                      <div className="absolute top-0 right-0 w-2 h-2 bg-gray-300 group-hover:bg-gray-500 transition-colors duration-200" />
                    </div>
                  )}
                </div>

                {/* Bottom Pane Content with Slide Animation */}
                <div
                  className="flex-shrink-0 overflow-hidden"
                  style={{
                    height: ((settings.outputPlacement === 'bottom' && showOutput) ||
                            (settings.outputPlacement === 'right' && aiFeedback.showFeedback))
                      ? `${resizing.outputPaneHeight}px`
                      : '0px',
                    // Disable transition during active resize to prevent snapping
                    transition: resizing.isResizingVertical || resizing.isResizingCorner
                      ? 'none'
                      : 'height 300ms ease-in-out',
                    willChange: resizing.isResizingVertical || resizing.isResizingCorner ? 'height' : 'auto',
                    opacity: ((settings.outputPlacement === 'bottom' && showOutput) ||
                            (settings.outputPlacement === 'right' && aiFeedback.showFeedback)) ? 1 : 0
                  }}
                >
                  {settings.outputPlacement === 'bottom' && showOutput ? (
                    <OutputPane
                      ref={outputPaneRef}
                      showOutput={showOutput}
                      onToggleOutput={() => setShowOutput(!showOutput)}
                      activeFile={activeFile}
                      code={code}
                      language={language}
                      placement="bottom"
                      executionHistory={executionHistory}
                      setExecutionHistory={setExecutionHistory}
                      currentOutput={currentOutput}
                      setCurrentOutput={setCurrentOutput}
                      classContext={classContext}
                      username={username}
                      onExecutionStateChange={setIsExecuting}
                    />
                  ) : (settings.outputPlacement === 'right' && aiFeedback.showFeedback) ? (
                    <AIFeedbackPane
                      showFeedback={aiFeedback.showFeedback}
                      onToggleFeedback={toggleAIFeedback}
                      placement="bottom"
                      feedbackItems={aiFeedback.items}
                      onRefreshFeedback={() => refreshAIFeedback()}
                      loading={aiFeedback.loading}
                    />
                  ) : null}
                </div>
              </>
            </div>

            {/* Right Pane (Output when outputPlacement='right', AI Feedback when outputPlacement='bottom') */}
            <>
              {/* Resize Handle */}
              <div
                className="vertical-resize-handle bg-gray-300 hover:bg-gray-400 cursor-col-resize flex items-center justify-center transition-colors duration-200 group flex-shrink-0"
                onMouseDown={resizing.handleHorizontalResizeStart}
                style={{
                  width: ((settings.outputPlacement === 'right' && showOutput) ||
                          (settings.outputPlacement === 'bottom' && aiFeedback.showFeedback)) ? '8px' : '0px',
                  transition: 'width 300ms ease-in-out',
                  overflow: 'hidden'
                }}
              >
                <div className="w-1 h-16 bg-gray-500 rounded-full group-hover:bg-gray-600"></div>
              </div>

              {/* Right Pane Content with Slide Animation */}
              <div
                className="flex-shrink-0 h-full overflow-hidden"
                style={{
                  width: ((settings.outputPlacement === 'right' && showOutput) ||
                          (settings.outputPlacement === 'bottom' && aiFeedback.showFeedback))
                    ? `${resizing.outputPaneWidth}%`
                    : '0%',
                  // Disable transition during active resize to prevent snapping
                  transition: resizing.isResizingHorizontal || resizing.isResizingCorner
                    ? 'none'
                    : 'width 300ms ease-in-out',
                  willChange: resizing.isResizingHorizontal || resizing.isResizingCorner ? 'width' : 'auto',
                  opacity: ((settings.outputPlacement === 'right' && showOutput) ||
                          (settings.outputPlacement === 'bottom' && aiFeedback.showFeedback)) ? 1 : 0
                }}
              >
                {settings.outputPlacement === 'right' && showOutput ? (
                  <OutputPane
                    ref={outputPaneRef}
                    showOutput={showOutput}
                    onToggleOutput={() => setShowOutput(!showOutput)}
                    activeFile={activeFile}
                    code={code}
                    language={language}
                    placement="right"
                    executionHistory={executionHistory}
                    setExecutionHistory={setExecutionHistory}
                    currentOutput={currentOutput}
                    setCurrentOutput={setCurrentOutput}
                    classContext={classContext}
                    username={username}
                    onExecutionStateChange={setIsExecuting}
                  />
                ) : (settings.outputPlacement === 'bottom' && aiFeedback.showFeedback) ? (
                  <AIFeedbackPane
                    showFeedback={aiFeedback.showFeedback}
                    onToggleFeedback={toggleAIFeedback}
                    placement="right"
                    feedbackItems={aiFeedback.items}
                    onRefreshFeedback={() => refreshAIFeedback()}
                    loading={aiFeedback.loading}
                  />
                ) : null}
              </div>
            </>
          </div>
        </div>
      </div>

      {/* Project Creation Modal */}
      <ProjectModal
        isOpen={showProjectModal}
        onClose={() => setShowProjectModal(false)}
        onCreateProject={handleCreateProject}
        loading={loading}
      />

      {/* File Creation Modal */}
      <FileModal
        isOpen={showFileModal}
        onClose={() => {
          setShowFileModal(false);
          setSelectedProjectForFile(null);
        }}
        onCreateFile={handleCreateFile}
        projects={projects}
        selectedProject={selectedProjectForFile ? projects.find(p => p.project_id === selectedProjectForFile) : null}
        loading={loading}
      />

      {/* Code Editor Settings Modal */}
      <CodeEditorSettings
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
        settings={settings}
        onSettingsChange={handleSettingsChange}
        onResetSettings={handleResetSettings}
      />

      {/* Rubric Sidebar */}
      {effectiveAssignmentId && (
        <RubricSidebar
          assignmentId={effectiveAssignmentId}
          componentId={effectiveComponentId}
          classContext={classContext}
          isOpen={showRubricSidebar}
          onClose={() => setShowRubricSidebar(false)}
          isInstructor={userRole === 'Instructor' || userRole === 'TA'}
        />
      )}

      {/* Submission History Sidebar */}
      {effectiveAssignmentId && (
        <SubmissionHistorySidebar
          assignmentId={effectiveAssignmentId}
          classContext={classContext}
          username={username}
          isOpen={showSubmissionsSidebar}
          onClose={() => setShowSubmissionsSidebar(false)}
          submissionType="code"
        />
      )}
    </div>
  );
};

export default CodeEditor;